/**
 * Customer Perception Insights Container
 * Main container component that orchestrates:
 * - Data fetching via RTK Query
 * - Tab switching between Flow Ribbons and Journey Board
 * - Stage selection and cohort drill-down
 */

import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, Paper, Skeleton, Tooltip } from "@mui/material";
import { MessageBar, MessageBarType } from "@fluentui/react";
import {
    useGetCustomerPerceptionInsightsQuery,
    useLazyGetCustomerPerceptionCohortQuery
} from "../../slices/CustomerPerception.slice";
import { FlowRibbonsSection } from "./FlowRibbonsSection";
import { JourneyBoardSection } from "./JourneyBoardSection";
import {
    ICustomerPerceptionContainerProps,
    PerceptionTab
} from "./CustomerPerception.interfaces";
import { THEME, PERCEPTION_TABS, STORAGE_KEYS } from "./CustomerPerception.constants";
import { CUSTOMER_PERCEPTION_STYLES } from "./CustomerPerception.styles";
import { logPageView, logEvent, logException } from "../../helper/AppInsightsHelper";
import { useLocalStorage } from "../../hooks/useLocalStorage";

/**
 * Customer Perception Insights Container Component
 */
export const CustomerPerceptionContainer: React.FC<ICustomerPerceptionContainerProps> = () => {
    // Persisted state
    const [activeTab, setActiveTab] = useLocalStorage<PerceptionTab>(
        STORAGE_KEYS.ACTIVE_TAB,
        PERCEPTION_TABS.FLOW_RIBBONS
    );
    const [selectedStage, setSelectedStage] = useLocalStorage<string>(
        STORAGE_KEYS.SELECTED_STAGE,
        ""
    );

    // Data fetching
    const {
        data: insightsData,
        isLoading: isInsightsLoading,
        isError: isInsightsError,
        error: insightsError
    } = useGetCustomerPerceptionInsightsQuery();

    const [
        fetchCohort,
        { data: cohortData, isLoading: isCohortLoading, isError: isCohortError }
    ] = useLazyGetCustomerPerceptionCohortQuery();

    // Log page view on mount
    useEffect(() => {
        logPageView("CustomerPerceptionInsights", "CustomerPerceptionContainer", {
            initialTab: activeTab
        });
    }, []);

    // Fetch cohort data when stage is selected
    useEffect(() => {
        if (selectedStage) {
            fetchCohort(selectedStage);
            logEvent(
                "Cohort selected",
                "CustomerPerceptionInsights",
                "CustomerPerceptionContainer",
                { stage: selectedStage }
            );
        }
    }, [selectedStage, fetchCohort]);

    // Auto-select first stage on Journey Board tab if none selected
    useEffect(() => {
        if (activeTab === PERCEPTION_TABS.JOURNEY_BOARD && !selectedStage && insightsData?.funnelCards.length) {
            const firstStage = insightsData.funnelCards[0]?.stage;
            if (firstStage) {
                setSelectedStage(firstStage);
            }
        }
    }, [activeTab, selectedStage, insightsData, setSelectedStage]);

    const handleTabChange = useCallback((tab: PerceptionTab) => {
        setActiveTab(tab);
        logEvent(
            "Tab changed",
            "CustomerPerceptionInsights",
            "CustomerPerceptionContainer",
            { tab }
        );
    }, [setActiveTab]);

    const handleStageSelect = useCallback((stage: string) => {
        setSelectedStage(stage);
        // If selecting a stage from Flow Ribbons, switch to Journey Board
        if (activeTab === PERCEPTION_TABS.FLOW_RIBBONS && stage) {
            setActiveTab(PERCEPTION_TABS.JOURNEY_BOARD);
        }
    }, [activeTab, setActiveTab, setSelectedStage]);

    // Error state
    if (isInsightsError) {
        logException(
            "CustomerPerceptionInsights",
            "CustomerPerceptionContainer",
            "fetchInsights",
            insightsError as Error,
            {}
        );
        return (
            <Box sx={{ p: 3 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    Failed to load Customer Perception Insights data. Please try again later.
                </MessageBar>
            </Box>
        );
    }

    // Loading state
    if (isInsightsLoading) {
        return (
            <Box sx={CUSTOMER_PERCEPTION_STYLES.root}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "12px", mb: 2 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: "8px", mb: 2 }} />
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} variant="rectangular" height={80} sx={{ flex: 1, borderRadius: "8px" }} />
                    ))}
                </Box>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: "12px" }} />
            </Box>
        );
    }

    const kpiTiles = insightsData?.kpiTiles || [];

    return (
        <Box sx={CUSTOMER_PERCEPTION_STYLES.root}>
            {/* Page Header */}
            <Paper elevation={0} sx={{ ...CUSTOMER_PERCEPTION_STYLES.header, position: "relative" }}>
                <Typography sx={CUSTOMER_PERCEPTION_STYLES.headerBreadcrumb}>
                    Pipeline & Deployment → Customers → Friction, Feedback & Patterns
                </Typography>
                <Typography sx={CUSTOMER_PERCEPTION_STYLES.headerTitle}>
                    Customers Perception Insights
                </Typography>
                <Box sx={CUSTOMER_PERCEPTION_STYLES.dataAsOf}>
                    Data as of {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Box>
            </Paper>

            {/* Narrative Box */}
            <Box sx={CUSTOMER_PERCEPTION_STYLES.narrativeBox}>
                <Typography sx={CUSTOMER_PERCEPTION_STYLES.narrativeText}>
                    <span style={{ textDecoration: "underline", fontWeight: 600 }}>The narrative in one line:</span>{" "}
                    Follow each customer from where they sit in the{" "}
                    <span style={{ fontWeight: 600, color: "#0078D4" }}>preview pipeline</span>,
                    through their{" "}
                    <span style={{ fontWeight: 600, color: "#0078D4" }}>deployment stage</span>,
                    to the{" "}
                    <span style={{ fontWeight: 600, color: "#0078D4" }}>engineering friction</span>{" "}
                    blocking them and what they are{" "}
                    <span style={{ textDecoration: "underline" }}>asking for</span>{" "}
                    to see <em>where</em> value is stuck and <em>why</em>.
                </Typography>
            </Box>

            {/* KPI Tiles */}
            <Box sx={CUSTOMER_PERCEPTION_STYLES.kpiRow}>
                {kpiTiles.map((kpi, idx) => (
                    <Box key={kpi.tile} sx={CUSTOMER_PERCEPTION_STYLES.kpiTile}>
                        <Box sx={{
                            ...(CUSTOMER_PERCEPTION_STYLES.kpiAccent as object),
                            background: idx === 0
                                ? "linear-gradient(90deg, #0078D4 0%, #60A5FA 100%)"
                                : idx === 1
                                    ? "linear-gradient(90deg, #8B5CF6 0%, #A78BFA 100%)"
                                    : "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)"
                        }} />
                        <Typography sx={CUSTOMER_PERCEPTION_STYLES.kpiValue}>{kpi.value}</Typography>
                        <Typography sx={CUSTOMER_PERCEPTION_STYLES.kpiLabel}>{kpi.tile}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Tab Navigation */}
            <Box sx={CUSTOMER_PERCEPTION_STYLES.tabContainer}>
                <Tooltip title="View the Sankey flow diagram showing Pipeline → Deployment → Friction" arrow>
                    <Box
                        sx={{
                            ...CUSTOMER_PERCEPTION_STYLES.tab as object,
                            ...(activeTab === PERCEPTION_TABS.FLOW_RIBBONS ? CUSTOMER_PERCEPTION_STYLES.tabActive as object : {})
                        }}
                        onClick={() => handleTabChange(PERCEPTION_TABS.FLOW_RIBBONS)}
                    >
                        <Box
                            sx={{
                                ...CUSTOMER_PERCEPTION_STYLES.tabNumber as object,
                                backgroundColor: activeTab === PERCEPTION_TABS.FLOW_RIBBONS ? "rgba(255,255,255,0.3)" : "#E6F2FA",
                                color: activeTab === PERCEPTION_TABS.FLOW_RIBBONS ? "#FFFFFF" : "#0078D4"
                            }}
                        >
                            1
                        </Box>
                        <Box>
                            <Typography sx={CUSTOMER_PERCEPTION_STYLES.tabText}>Flow Ribbons</Typography>
                            <Typography sx={CUSTOMER_PERCEPTION_STYLES.tabSubtext}>Pipeline → Deployment → Friction</Typography>
                        </Box>
                    </Box>
                </Tooltip>
                <Tooltip title="Drill into a cohort to see deployment mix, friction, asks, and feedback" arrow>
                    <Box
                        sx={{
                            ...CUSTOMER_PERCEPTION_STYLES.tab as object,
                            ...(activeTab === PERCEPTION_TABS.JOURNEY_BOARD ? CUSTOMER_PERCEPTION_STYLES.tabActive as object : {})
                        }}
                        onClick={() => handleTabChange(PERCEPTION_TABS.JOURNEY_BOARD)}
                    >
                        <Box
                            sx={{
                                ...CUSTOMER_PERCEPTION_STYLES.tabNumber as object,
                                backgroundColor: activeTab === PERCEPTION_TABS.JOURNEY_BOARD ? "rgba(255,255,255,0.3)" : "#E6F2FA",
                                color: activeTab === PERCEPTION_TABS.JOURNEY_BOARD ? "#FFFFFF" : "#0078D4"
                            }}
                        >
                            2
                        </Box>
                        <Box>
                            <Typography sx={CUSTOMER_PERCEPTION_STYLES.tabText}>Journey Board</Typography>
                            <Typography sx={CUSTOMER_PERCEPTION_STYLES.tabSubtext}>Funnel + cohort drilldown</Typography>
                        </Box>
                    </Box>
                </Tooltip>
            </Box>

            {/* Tab Content */}
            {activeTab === PERCEPTION_TABS.FLOW_RIBBONS ? (
                <FlowRibbonsSection
                    sankeyNodes={insightsData?.sankeyNodes || []}
                    sankeyLinks={insightsData?.sankeyLinks || []}
                    frictionLegend={insightsData?.frictionLegend || []}
                    customerChips={insightsData?.customerChips || []}
                    cohortCustomers={cohortData?.cohortCustomers}
                    isLoading={isInsightsLoading}
                    onStageClick={handleStageSelect}
                    selectedStage={selectedStage}
                    livePreviewUsage={cohortData?.livePreviewUsage}
                />
            ) : (
                <JourneyBoardSection
                    funnelCards={insightsData?.funnelCards || []}
                    cohortData={cohortData}
                    isLoading={isInsightsLoading}
                    isCohortLoading={isCohortLoading}
                    selectedStage={selectedStage}
                    onStageSelect={handleStageSelect}
                />
            )}
        </Box>
    );
};

export default CustomerPerceptionContainer;
